import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import {
  ChatText,
  CheckCircle,
  CircleNotch,
  Database,
  Envelope,
  FileText,
  Globe,
  Image,
  Microphone,
  WebhooksLogo
} from '@phosphor-icons/react';

const InputNode = memo(({ data, selected }) => {
  const iconMap = {
    text: ChatText,
    audio: Microphone,
    file_pdf: FileText,
    file_docx: FileText,
    file_csv: Database,
    file_xlsx: Database,
    webhook: WebhooksLogo,
    api_payload: WebhooksLogo,
    url: Globe,
    image: Image,
    email: Envelope
  };

  const Icon = iconMap[data.inputType] || ChatText;
  const executionStatus = data.executionStatus;

  return (
    <div
      data-testid="flow-node-input"
      className={`
        group relative min-w-[190px] max-w-[230px] rounded-[10px] overflow-hidden
        bg-[var(--surface-2)] border-l-[3px] border transition-all duration-150
        ${executionStatus === 'processing'
          ? 'border-l-blue-400 border-blue-400/40'
          : executionStatus === 'success'
            ? 'border-l-emerald-400 border-emerald-400/40'
            : selected
              ? 'border-l-blue-400 border-[var(--border-strong)]'
              : 'border-l-blue-500 border-[var(--border-default)] hover:border-[var(--border-strong)]'}
      `}
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 px-3 pt-3 pb-2">
        <div className={`flex-shrink-0 w-[34px] h-[34px] rounded-lg border flex items-center justify-center
          ${executionStatus === 'success'
            ? 'bg-emerald-500/10 border-emerald-500/20'
            : 'bg-blue-500/10 border-blue-500/20'}`}
        >
          {executionStatus === 'processing' ? (
            <CircleNotch size={20} weight="bold" className="text-blue-400 animate-spin" />
          ) : executionStatus === 'success' ? (
            <CheckCircle size={20} weight="duotone" className="text-emerald-400" />
          ) : (
            <Icon size={20} weight="duotone" className="text-blue-400" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-white truncate leading-tight">{data.label}</p>
          <span className="text-[10px] text-blue-400/60 font-medium uppercase tracking-wider">Input</span>
        </div>
      </div>

      {/* Body - status */}
      <div className="px-3 pb-2.5 pt-0.5 border-t border-white/[0.04]">
        <div className="flex items-center gap-1.5">
          {executionStatus === 'processing' ? (
            <span className="text-[10px] text-blue-400 font-medium animate-pulse">Processing...</span>
          ) : executionStatus === 'success' ? (
            <span className="text-[10px] text-emerald-400 font-medium">Complete</span>
          ) : data.required ? (
            <span className="inline-flex items-center px-1.5 py-px text-[9px] font-semibold rounded bg-blue-500/15 text-blue-400 border border-blue-500/20 uppercase tracking-wider">Required</span>
          ) : (
            <span className="text-[10px] text-slate-500">Ready</span>
          )}
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Right}
        className="!w-[10px] !h-[10px] !border-2 !right-[-6px] transition-colors
          !bg-blue-400 !border-blue-300/50 hover:!bg-blue-300"
      />
    </div>
  );
});

InputNode.displayName = 'InputNode';
export default InputNode;
